import React from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler
);

interface ReasonEvictionData {
  total_evictions_individuals: number;
  total_evictions_households: number;
}

interface EvictionsByTimePeriod {
  [timePeriod: string]: {
    [reason: string]: ReasonEvictionData;
  };
}

interface EvictionsReasonMonthLineChartProps {
  data?: EvictionsByTimePeriod | null;
  title?: string;
  timePeriodLabel?: string; // e.g., "Year" or "Month"
}

const colorPalette = [
  "#D32F2F", // Strong Red
  "#1976D2", // Bold Blue
  "#388E3C", // Dark Green
  "#FBC02D", // Bright Yellow
  "#8E24AA", // Deep Purple
  "#0288D1", // Light Blue
  "#7B1FA2", // Purple
  "#FF9800", // Orange
  "#009688", // Teal
  "#C2185B", // Pink
  "#0288D1", // Sky Blue
  "#8E44AD", // Violet
];

const monthOrder = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const EvictionsReasonMonthLineChart: React.FC<EvictionsReasonMonthLineChartProps> = ({
  data,
  title = "Evictions by Reason (Individuals) Over Time",
  timePeriodLabel = "Time Period",
}) => {
  const hasData =
    data &&
    typeof data === "object" &&
    Object.keys(data).length > 0;

  if (!hasData) {
    return (
      <div className="p-2 bg-white rounded-md flex items-center justify-center">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <svg
            className="w-5 h-5 animate-spin text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          Loading chart data...
        </div>
      </div>
    );
  }

  // Sort time periods (years or months)
  const timePeriods = Object.keys(data).sort((a, b) => {
    // If it's month data, sort by month order
    if (monthOrder.includes(a) && monthOrder.includes(b)) {
      return monthOrder.indexOf(a) - monthOrder.indexOf(b);
    }
    // Otherwise assume numeric sorting (for years)
    return a.localeCompare(b);
  });

  const firstPeriod = timePeriods[0];

  const reasons =
    firstPeriod && data[firstPeriod]
      ? Object.keys(data[firstPeriod])
      : [];

  const datasets = reasons.map((reason, index) => {
    const dataPoints = timePeriods.map((period) => {
      const periodData = data[period] || {};
      return periodData[reason]?.total_evictions_individuals || 0;
    });

    return {
      label: reason,
      data: dataPoints,
      borderColor: colorPalette[index % colorPalette.length],
      backgroundColor: colorPalette[index % colorPalette.length] + "33", // Add opacity
      borderWidth: 1,
      pointBackgroundColor: "#fff",
      pointBorderColor: colorPalette[index % colorPalette.length],
      pointBorderWidth: 1,
      pointRadius: 3,
      pointHoverRadius: 5,
      fill: false,
      tension: 0.1,
    };
  });

  const chartData = {
    labels: timePeriods,
    datasets,
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false,
        text: title,
        font: {
          size: 12,
          weight: "bold",
        },
        color: "#333",
        padding: { top: 0, bottom: 10 },
      },
      legend: {
        display: true,
        position: "top",
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          padding: 10,
          font: {
            size: 10,
          },
          usePointStyle: true,
        },
      },
      tooltip: {
        backgroundColor: "#fff",
        borderColor: "#ddd",
        borderWidth: 1,
        titleColor: "#333",
        bodyColor: "#333",
        padding: 10,
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${context.raw?.toLocaleString()}`;
          },
          title: (context) => {
            return `${timePeriodLabel}: ${context[0].label}`;
          }
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 10,
          },
          color: "#666",
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(180, 180, 180, 0.1)",
        },
        ticks: {
          font: {
            size: 10,
          },
          color: "#666",
          callback: function (value) {
            if (typeof value === "number") {
              if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
              if (value >= 1_000) return (value / 1_000).toFixed(0) + 'K';
              return value.toString();
            }
            return value;
          },
        },
      },
    },
  };

  return (
    <div className="p-2 bg-white rounded-md">
      <div style={{ minHeight: "350px", width: "100%" }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default EvictionsReasonMonthLineChart;